import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config/environment';

const WhatsAppManagement = () => {
  const [status, setStatus] = useState('checking');
  const [qrCode, setQrCode] = useState(null);
  const [isPairingEnabled, setIsPairingEnabled] = useState(false); 
  const [groups, setGroups] = useState([]);

  // Sprawdzanie statusu WhatsApp
  const checkStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setStatus(data.status);

      // Pobierz kod QR tylko jeśli parowanie jest włączone i status jest disconnected
      if (data.status === 'disconnected' && isPairingEnabled) {
        getQrCode();
      }
    } catch (error) {
      console.error('Error checking WhatsApp status:', error);
      setStatus('error');
    }
  };

  // Pobieranie kodu QR
  const getQrCode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp/qr`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.qr) {
        setQrCode(data.qr);
        
        // Utwórz obraz QR kodu używając biblioteki qrcode-terminal
        const qrImage = document.createElement('canvas');
        // Konwersja kodu QR na obraz
        const qrCodeImage = await new Promise((resolve) => {
          const qr = require('qrcode');
          qr.toCanvas(qrImage, data.qr, (error) => {
            if (error) console.error(error);
            resolve(qrImage.toDataURL());
          });
        });
        setQrCode(qrCodeImage);
      }
    } catch (error) {
      console.error('Error getting QR code:', error);
    }
  };

  // Obsługa włączania/wyłączania parowania
  const handleTogglePairing = async () => {
    try {
      const newPairingState = !isPairingEnabled;
      const response = await fetch(`${API_BASE_URL}/api/whatsapp/toggle-pairing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: newPairingState })
      });
      
      if (response.ok) {
        setIsPairingEnabled(newPairingState);
        toast.success(`Parowanie ${newPairingState ? 'włączone' : 'wyłączone'}`);
        if (!newPairingState) {
          setQrCode(null); // Wyczyść kod QR po wyłączeniu parowania
        }
      } else {
        toast.error('Błąd podczas zmiany stanu parowania');
      }
    } catch (error) {
      console.error('Error toggling pairing:', error);
      toast.error('Błąd podczas zmiany stanu parowania');
    }
  };

  // Restart usługi WhatsApp
  const handleRestart = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp/restart`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      toast.info(data.message);
      checkStatus();
    } catch (error) {
      console.error('Error restarting WhatsApp:', error);
      toast.error('Błąd podczas restartowania usługi WhatsApp');
    }
  };

  // Wysłanie wiadomości testowej
  const handleTestMessage = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp/test-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Wiadomość testowa została wysłana');
      } else {
        toast.error('Błąd podczas wysyłania wiadomości testowej');
      }
    } catch (error) {
      console.error('Error sending test message:', error);
      toast.error('Błąd podczas wysyłania wiadomości testowej');
    }
  };

  const handleShowGroups = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/whatsapp/show-groups`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const data = await response.json();
        
        if (data.success) {
            setGroups(data.groups);
            if (data.groups.length > 0) {
                toast.success('Pobrano listę grup');
            } else {
                toast.info('Brak dostępnych grup');
            }
        } else {
            toast.info(data.message);
        }
    } catch (error) {
        console.error('Error fetching groups:', error);
        toast.error('Błąd podczas pobierania grup');
    }
};

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 10000); // Sprawdzaj status co 10 sekund
    return () => clearInterval(interval);
  }, [isPairingEnabled]); // Dodano isPairingEnabled jako zależność

  const handleClearSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp/clear-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Sesja została wyczyszczona');
        // Automatycznie zrestartuj po wyczyszczeniu
        await handleRestart();
      } else {
        toast.error(data.error || 'Błąd podczas czyszczenia sesji');
      }
    } catch (error) {
      console.error('Error clearing session:', error);
      toast.error('Błąd podczas czyszczenia sesji');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-2xl font-bold mb-4">Status WhatsApp</h2>
        
        {/* Status indicator */}
        <div className="flex items-center space-x-4 mb-6">
          <div className={`h-3 w-3 rounded-full ${
            status === 'connected' ? 'bg-green-500' :
            status === 'disconnected' ? 'bg-red-500' :
            'bg-yellow-500'
          }`} />
          <span className="capitalize font-medium">
            {status === 'connected' ? 'Połączono' :
             status === 'disconnected' ? 'Rozłączono' :
             'Sprawdzanie...'}
          </span>
        </div>

        {/* QR Code section */}
        {status === 'disconnected' && qrCode && isPairingEnabled && (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Zeskanuj kod QR aby połączyć WhatsApp</h3>
            <div className="flex justify-center bg-white p-4 rounded-lg border">
              <img src={qrCode} alt="WhatsApp QR Code" className="max-w-xs" />
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Otwórz WhatsApp na telefonie i zeskanuj ten kod, aby połączyć się z systemem
            </p>
          </div>
        )}
        {/* Groups section */}
          {groups.length > 0 && (
              <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3">Lista grup WhatsApp</h3>
                  <div className="bg-gray-50 p-4 rounded-lg border">
                      <table className="min-w-full">
                          <thead>
                              <tr>
                                  <th className="text-left pb-2">Nazwa</th>
                                  <th className="text-left pb-2">ID</th>
                                  <th className="text-left pb-2">Liczba uczestników</th>
                              </tr>
                          </thead>
                          <tbody>
                              {groups.map((group, index) => (
                                  <tr key={group.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="py-2">{group.name}</td>
                                      <td className="py-2">
                                          <span className="font-mono text-sm">{group.id}</span>
                                      </td>
                                      <td className="py-2">{group.participantsCount}</td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>
              </div>
          )}
        {/* Action buttons */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleRestart}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Restart WhatsApp
          </button>
          <button
            onClick={handleTestMessage}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
            disabled={status !== 'connected'}
          >
            Wyślij wiadomość testową
          </button>
          <button
            onClick={handleTogglePairing}
            className={`${
              isPairingEnabled ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
            } text-white px-4 py-2 rounded transition-colors`}
          >
            {isPairingEnabled ? 'Wyłącz parowanie' : 'Włącz parowanie'}
          </button>
          {status === 'disconnected' && !qrCode && isPairingEnabled && (
            <button
              onClick={getQrCode}
              className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 transition-colors"
            >
              Pokaż kod QR
            </button>
          )}
          <button
            onClick={handleClearSession}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
          >
            Wyczyść sesję
          </button>
          <button
            onClick={handleShowGroups}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 transition-colors"
          >
            Pokaż ID grup
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppManagement;